import { matchPath } from "react-router-dom";
import { AskScope } from "@mosaic/shared";
import { chooseDocument, type ChoosableDocument } from "./documents.js";

/**
 * What the page in front of the owner is a question about, or null where a question has
 * no subject.
 *
 * Ask is a panel of the shell, so the shell answers *asked of what*, and it reads the
 * route: the owner has already said there which subject they are looking at, and a scope
 * picker would ask a second time and could disagree.
 *
 * **Three routes have a subject and the rest deliberately do not.** A situation, a company
 * and a filing are each something to ask about; Search and Ops look at Mosaic rather than
 * at a separation.
 *
 * `library` (the whole corpus) is a real scope in the schema and is never produced here:
 * every route this shell renders is somewhere in particular.
 *
 * Matching goes through the router's own `matchPath` rather than a regex of the same
 * shape, so the route table in `App.tsx` and this read each path the same way.
 */
export function askScopeFor(
  pathname: string,
  params: URLSearchParams,
  /**
   * The reader's filing, once it has been fetched. Only the document scope needs it,
   * and the shell fetches it on the reader route alone.
   */
  documents?: readonly ChoosableDocument[],
): AskScope | null {
  const situation = matchPath("/situations/:id", pathname);
  if (situation?.params.id) return parse({ kind: "situation", id: situation.params.id });

  const company = matchPath("/companies/:cik", pathname);
  if (company?.params.cik) return parse({ kind: "company", cik: company.params.cik });

  const accession = readerAccession(pathname);
  if (accession) {
    /**
     * **A document scope names one document, and the URL usually does not.** Most links
     * carry `doc=<uuid>`, which the scope has no room for, and with nothing named the reader
     * shows the filing's largest extracted document. So this asks the same `chooseDocument`
     * the reader asks: the answer has to be the document on screen.
     *
     * Until the filing has been fetched there is no scope. `whole` loads and prices the
     * document the scope names, so naming one the reader is not showing would preload the
     * wrong text; an absent scope is a pane that does not open for a moment.
     */
    const named = Number(params.get("sequence"));
    const chosen = chooseDocument(documents, {
      documentId: params.get("doc"),
      sequence: Number.isInteger(named) ? named : null,
    });
    if (chosen) return parse({ kind: "document", accession, sequence: chosen.sequence });
  }

  return null;
}

/**
 * The filing the reader is open on, or null anywhere else.
 *
 * The shell calls it to decide whether to fetch the filing `askScopeFor` needs for a
 * document scope.
 */
export function readerAccession(pathname: string): string | null {
  return matchPath("/filings/:accession", pathname)?.params.accession ?? null;
}

/**
 * Through the shared schema rather than trusting the URL.
 *
 * A route parameter is whatever was typed or pasted, and `Cik` and `Accession` have real
 * shapes. A malformed one becomes no panel rather than a scope the server answers 400 to,
 * which would reach the owner as a failed question.
 *
 * @internal
 */
function parse(candidate: unknown): AskScope | null {
  const parsed = AskScope.safeParse(candidate);
  return parsed.success ? parsed.data : null;
}

/**
 * The identity of a subject, for keying the panel.
 *
 * Navigating between two subjects with the panel open must start a fresh thread: a thread
 * that quietly changed subject would cite the wrong filings. The kind is part of the key so
 * subjects of different kinds never collide.
 */
export function askScopeKey(scope: AskScope): string {
  switch (scope.kind) {
    case "library":
      return "library";
    case "situation":
      return `situation:${scope.id}`;
    case "company":
      return `company:${scope.cik}`;
    case "document":
      return `document:${scope.accession}:${scope.sequence}`;
  }
}

/**
 * The scope as preflight query parameters: the inverse of `toScope` in
 * `server/src/routes/ask.ts`.
 *
 * A second expression of one mapping, unavoidably, since the two halves are on opposite
 * sides of a wire. Kept beside the derivation so this side's half is in one file.
 */
export function askScopeQuery(scope: AskScope): Record<string, string | number | undefined> {
  switch (scope.kind) {
    case "library":
      return {};
    case "situation":
      return { situationId: scope.id };
    case "company":
      return { cik: scope.cik };
    case "document":
      return { accession: scope.accession, sequence: scope.sequence };
  }
}

/**
 * What the way out of the panel is called on a phone, where it is a strip of vertical
 * text rather than a header control.
 *
 * The kind rather than the subject's name: the name belongs to the page, and the one list
 * the shell holds is filtered, so a situation the current chips exclude would come back
 * unnamed. The kind still says where you land.
 */
export function askScopeNoun(scope: AskScope): string {
  switch (scope.kind) {
    case "library":
      return "Library";
    case "situation":
      return "Situation";
    case "company":
      return "Company";
    case "document":
      return "Filing";
  }
}
